import { IdName } from 'src/app/shared/models/id-name';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { insertItem, patch, updateItem } from '@ngxs/store/operators';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { UserDocumentsRoutes } from 'src/app/core/enums/routes.enum';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { ApiResponse } from 'src/app/core/models/api-response';
import { MenuTreeStructureState } from 'src/app/core/state/menu-tree-structure.state';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { IDocumentVersion } from '../models/document-version.model';
import { findDocumentVersionIndex } from '../functions/document-version-resolution';
import {
  IApiResponseGroupDocuments,
  IDocument,
  IDocumentGroupData,
  IDocumentNavigationResponse,
  IDocumentOnNode,
} from '../models/document.model';
import { DocumentApiService } from '../services/document-api.service';
import {
  LoadAdminDocumentReadReceiptRequests,
  ApproveDocument,
  CancelDocumentReadReceiptRequest,
  ConfirmDocumentRead,
  CreateDocument,
  DeleteDocumentReadReceipt,
  DeleteDocument,
  DisableDocument,
  EnableDocument,
  LoadCurrentDepartmentReadReceiptSettings,
  LoadDocumentReadReceipts,
  LoadDocumentById,
  LoadDocumentVersion,
  LoadDocumentVersions,
  LoadDocuments,
  LoadDocumentsByMenu,
  LoadDocumentsAwaitingReadReceipt,
  LoadUserDocumentReadReceipts,
  PrintDocument,
  PrintDocumentTree,
  RejectDocument,
  RestoreDocument,
  UpdateDocument,
  RequestChangeOnDocument,
  AcceptDocumentChangeRequest,
  RejectDocumentChangeRequest,
  LoadDocumentCounts,
  LoadDocumentNavigation,
  LoadDocumentsForIO,
  PrintTextControlOnly,
  ClearDocumentsByMenu,
  ClearCurrentDocument,
  SetRevisionNumber,
  UpdateDepartmentsReadReceiptSettings,
  UpdateDocumentReadReceipt,
} from './document.actions';
import { IDocumentApproval } from '../models/document-approval.model';
import { ApprovalStatus } from '../enums/approval-status.enum';
import { SAVE_TYPE_ROUTE } from '../enums/save-type.enum';
import { ChangeRequestStatus } from '../enums/change-request-status.enum';
import { IDocumentCounts } from '../models/document-counts.model';
import {
  IAdminDocumentReadReceiptRequest,
  IDocumentReadReceipt,
  IUserDocumentReadReceipt,
} from '../models/document-read-receipt.model';
import { DOCUMENT_STATUS_LABELS, DocumentStatus } from '../enums/document-status.enum';
import { IReadReceiptSettingsResponse } from '../models/read-receipt-settings.model';

export type DocumentVersionRef = { documentId: string; versions: IDocumentVersion[] };
export interface DocumentStateModel {
  documents: IDocument[];
  documentsTotalPages: number;
  groupedDocuments: IDocumentGroupData[];
  documentsAwaitingReadReceipt: IDocumentOnNode[];
  documentVersions: DocumentVersionRef[];
  documentPrintFile?: Blob;
  textControlPrintFile?: Blob;
  currentDepartmentReadReceiptSettings?: IReadReceiptSettingsResponse | undefined;
  departmentReadReceiptSettings: IReadReceiptSettingsResponse[];
  currentDocumentReadReceipts?: IDocumentReadReceipt[] | undefined;
  adminReadReceiptRequests: IAdminDocumentReadReceiptRequest[];
  adminReadReceiptRequestsTotalPages: number;
  userDocumentReadReceipts: IUserDocumentReadReceipt[];
  userDocumentReadReceiptsTotalPages: number;
  documentCounts?: IDocumentCounts | undefined;
  documentsForIO: IdName[];
  currentDocument?: IDocument | undefined;
  documentNavigation?: IDocumentNavigationResponse | undefined;
}

const DOCUMENT_STATE_TOKEN = new StateToken<DocumentStateModel>('document');

@State<DocumentStateModel>({
  name: DOCUMENT_STATE_TOKEN,
  defaults: {
    documents: [],
    documentsTotalPages: 0,
    groupedDocuments: [],
    documentsAwaitingReadReceipt: [],
    documentVersions: [],
    currentDepartmentReadReceiptSettings: undefined,
    departmentReadReceiptSettings: [],
    currentDocumentReadReceipts: undefined,
    adminReadReceiptRequests: [],
    adminReadReceiptRequestsTotalPages: 0,
    userDocumentReadReceipts: [],
    userDocumentReadReceiptsTotalPages: 0,
    documentCounts: undefined,
    documentsForIO: [],
    currentDocument: undefined,
    documentNavigation: undefined,
  },
})
@Injectable()
export class DocumentState {
  constructor(
    private service: DocumentApiService,
    private store: Store
  ) {}

  @Action(LoadDocuments, { cancelUncompleted: true })
  loadDocuments(
    { getState, setState }: StateContext<DocumentStateModel>,
    { filter, query }: LoadDocuments
  ) {
    return this.service.loadAllDocuments(filter, query).pipe(
      tap((response: ApiResponse<IDocument[]>) => {
        const state = getState();
        setState({
          ...state,
          documents: response.data,
          documentsTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadDocumentsByMenu, { cancelUncompleted: true })
  loadDocumentsByMenu(
    { getState, setState }: StateContext<DocumentStateModel>,
    { filter }: LoadDocumentsByMenu
  ) {
    return this.service.loadDocumentsByStructure(filter).pipe(
      tap((response: IApiResponseGroupDocuments) => {
        const state = getState();
        setState({
          ...state,
          groupedDocuments: response.groupData,
          documentsTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadDocumentsAwaitingReadReceipt, { cancelUncompleted: true })
  loadDocumentsAwaitingReadReceipt(
    { getState, setState }: StateContext<DocumentStateModel>,
    { filter }: LoadDocumentsAwaitingReadReceipt
  ) {
    setState({
      ...getState(),
      documentsAwaitingReadReceipt: [],
    });

    return this.service.loadDocumentsByStructure(filter).pipe(
      tap((response: IApiResponseGroupDocuments) => {
        const state = getState();
        setState({
          ...state,
          documentsAwaitingReadReceipt: response.groupData.flatMap((group) => group.itemsOnNode),
        });
      })
    );
  }

  @Action(LoadAdminDocumentReadReceiptRequests, { cancelUncompleted: true })
  loadAdminDocumentReadReceiptRequests(
    { getState, setState }: StateContext<DocumentStateModel>,
    { filter }: LoadAdminDocumentReadReceiptRequests
  ) {
    setState({
      ...getState(),
      adminReadReceiptRequests: [],
      adminReadReceiptRequestsTotalPages: 0,
    });

    return this.service.getAdminReadReceiptRequests(filter).pipe(
      tap((response: ApiResponse<IAdminDocumentReadReceiptRequest[]>) => {
        const state = getState();
        setState({
          ...state,
          adminReadReceiptRequests: response.data,
          adminReadReceiptRequestsTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadUserDocumentReadReceipts, { cancelUncompleted: true })
  loadUserDocumentReadReceipts(
    { getState, setState }: StateContext<DocumentStateModel>,
    { userId, filter }: LoadUserDocumentReadReceipts
  ) {
    setState({
      ...getState(),
      userDocumentReadReceipts: [],
      userDocumentReadReceiptsTotalPages: 0,
    });

    return this.service.getUserDocumentReadReceipts(userId, filter).pipe(
      tap((response: ApiResponse<IUserDocumentReadReceipt[]>) => {
        const state = getState();
        setState({
          ...state,
          userDocumentReadReceipts: response.data,
          userDocumentReadReceiptsTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadDocumentById, { cancelUncompleted: true })
  loadDocumentById(
    { getState, setState }: StateContext<DocumentStateModel>,
    { documentId, includeDeleted, forcedVersionId }: LoadDocumentById
  ) {
    const { documents } = getState();
    const exist = documents.some((i) => i.id === documentId);
    return this.service.loadDocumentById(documentId, includeDeleted, forcedVersionId).pipe(
      tap((document: IDocument) => {
        setState(
          patch({
            documents: exist
              ? updateItem<IDocument>((toUpdate) => toUpdate?.id === documentId, document)
              : insertItem<IDocument>(document),
            currentDocument: document,
          })
        );
      })
    );
  }

  @Action(LoadCurrentDepartmentReadReceiptSettings, { cancelUncompleted: true })
  loadCurrentDepartmentReadReceiptSettings({
    getState,
    setState,
  }: StateContext<DocumentStateModel>) {
    setState({
      ...getState(),
      currentDepartmentReadReceiptSettings: undefined,
      departmentReadReceiptSettings: [],
    });

    return this.service.getDepartmentsReadReceiptSettings().pipe(
      tap((departmentReadReceiptSettings: IReadReceiptSettingsResponse[]) => {
        const state = getState();
        setState({
          ...state,
          departmentReadReceiptSettings,
          currentDepartmentReadReceiptSettings: this.getCurrentDepartmentReadReceiptSettings(
            departmentReadReceiptSettings
          ),
        });
      })
    );
  }

  @Action(LoadDocumentsForIO)
  loadDocumentsForIO({ getState, setState }: StateContext<DocumentStateModel>) {
    return this.service.loadDocumentsForIO().pipe(
      tap((response: IdName[]) => {
        const state = getState();
        setState({
          ...state,
          documentsForIO: response,
        });
      })
    );
  }

  @Action(CreateDocument, { cancelUncompleted: true })
  @OnSuccess({ message: 'documentCreated' })
  createDocument(ctx: StateContext<DocumentStateModel>, { document }: CreateDocument) {
    return this.service.createDocument(document).pipe(
      tap((response: IDocument) => {
        ctx.setState(
          patch({
            documents: insertItem<IDocument>(response),
          })
        );
        const menuId =
          this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId) ?? document.menuId;
        const url = `${UserDocumentsRoutes.Overview}/${menuId}/${
          SAVE_TYPE_ROUTE[document.saveType]
        }`;
        redirectAndBypassUnsavedDataCheck(url);
      })
    );
  }

  @Action(ConfirmDocumentRead, { cancelUncompleted: true })
  @OnSuccess({ message: 'documentReadConfirmed' })
  confirmDocumentRead(
    { getState, setState }: StateContext<DocumentStateModel>,
    { documentId }: ConfirmDocumentRead
  ) {
    return this.service.confirmDocumentRead(documentId).pipe(
      tap(() => {
        const state = getState();
        setState({
          ...state,
          documents: state.documents.map((document) =>
            document.id === documentId ? { ...document, hasReadReceiptToRead: false } : document
          ),
          currentDocument:
            state.currentDocument?.id === documentId
              ? { ...state.currentDocument, hasReadReceiptToRead: false }
              : state.currentDocument,
        });
      })
    );
  }

  @Action(LoadDocumentReadReceipts, { cancelUncompleted: true })
  loadDocumentReadReceipts(
    { getState, setState }: StateContext<DocumentStateModel>,
    { documentId }: LoadDocumentReadReceipts
  ) {
    setState({
      ...getState(),
      currentDocumentReadReceipts: undefined,
    });

    return this.service.getDocumentReadReceipts(documentId).pipe(
      tap((readReceipts: IDocumentReadReceipt[]) => {
        const state = getState();
        setState({
          ...state,
          currentDocumentReadReceipts: readReceipts,
        });
      })
    );
  }

  @Action(UpdateDocumentReadReceipt, { cancelUncompleted: true })
  @OnSuccess({ message: 'readReceiptRecipientsUpdated' })
  updateDocumentReadReceipt(
    ctx: StateContext<DocumentStateModel>,
    { documentId, readReceiptId, readReceipt }: UpdateDocumentReadReceipt
  ) {
    return this.service.updateDocumentReadReceipt(documentId, readReceiptId, readReceipt).pipe(
      tap(() => {
        ctx.dispatch(new LoadDocumentReadReceipts(documentId));
      })
    );
  }

  @Action(CancelDocumentReadReceiptRequest, { cancelUncompleted: true })
  @OnSuccess({ message: 'readReceiptRequestCancelled' })
  cancelDocumentReadReceiptRequest(
    ctx: StateContext<DocumentStateModel>,
    { documentId, requestId }: CancelDocumentReadReceiptRequest
  ) {
    return this.service.cancelDocumentReadReceiptRequest(documentId, requestId).pipe(
      tap(() => {
        const state = ctx.getState();

        ctx.setState({
          ...state,
          currentDocumentReadReceipts: state.currentDocumentReadReceipts?.map((readReceipt) => ({
            ...readReceipt,
            readReceiptRequests: readReceipt.readReceiptRequests.map((request) =>
              request.id === requestId ? { ...request, isCancelled: true } : request
            ),
          })),
        });
      }),
      switchMap(() =>
        ctx.dispatch(
          new LoadDocumentById(documentId, false, ctx.getState().currentDocument?.versionId)
        )
      )
    );
  }

  @Action(DeleteDocumentReadReceipt, { cancelUncompleted: true })
  @OnSuccess({ message: 'readReceiptDeleted' })
  deleteDocumentReadReceipt(
    ctx: StateContext<DocumentStateModel>,
    { documentId, readReceiptId }: DeleteDocumentReadReceipt
  ) {
    return this.service.deleteDocumentReadReceipt(readReceiptId).pipe(
      tap(() => {
        ctx.dispatch([new LoadDocumentById(documentId), new LoadDocumentReadReceipts(documentId)]);
      })
    );
  }

  @Action(UpdateDocument, { cancelUncompleted: true })
  @OnSuccess({ message: 'documentUpdated' })
  updateDocument(ctx: StateContext<DocumentStateModel>, { document }: UpdateDocument) {
    return this.service.updateDocument(document).pipe(
      tap(() => {
        const menuId =
          this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId) ?? document.menuId;
        const url = `${UserDocumentsRoutes.Overview}/${menuId}/${
          SAVE_TYPE_ROUTE[document.saveType]
        }`;
        redirectAndBypassUnsavedDataCheck(url);
      })
    );
  }

  @Action(DeleteDocument, { cancelUncompleted: true })
  @OnSuccess({ message: 'documentDeleted' })
  deleteDocument(ctx: StateContext<DocumentStateModel>, { documentId, status }: DeleteDocument) {
    return this.service.deleteDocument(documentId).pipe(
      tap(() => {
        ctx.dispatch([new LoadDocuments(), new ResetCurrentFilter()]);
        const menuId = this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId);
        let url = `${UserDocumentsRoutes.Main}`;
        let statusRoute = DOCUMENT_STATUS_LABELS[status];
        if (status === DocumentStatus.Expired || status === DocumentStatus.Expiring) {
          statusRoute = DOCUMENT_STATUS_LABELS[DocumentStatus.Published];
        }
        if (menuId) {
          url = `${UserDocumentsRoutes.Overview}/${menuId}/${statusRoute}`;
        }
        redirectAndBypassUnsavedDataCheck(url);
      })
    );
  }

  @Action(LoadDocumentVersions, { cancelUncompleted: true })
  loadDocumentVersions(
    { getState, setState }: StateContext<DocumentStateModel>,
    { documentId, includeDeleted }: LoadDocumentVersions
  ) {
    const { documentVersions } = getState();
    const exist = documentVersions.some((i) => i.documentId === documentId);
    return this.service.loadDocumentVersions(documentId, includeDeleted).pipe(
      tap((versions: IDocumentVersion[]) => {
        const versionRef = { documentId, versions };
        setState(
          patch({
            documentVersions: exist
              ? updateItem<DocumentVersionRef>((ref) => ref?.documentId === documentId, versionRef)
              : insertItem<DocumentVersionRef>(versionRef),
          })
        );
      })
    );
  }

  @Action(LoadDocumentVersion, { cancelUncompleted: true })
  loadDocumentVersion(
    { getState, setState }: StateContext<DocumentStateModel>,
    { documentId, documentVersionId, includeDeleted, versionDisambiguator }: LoadDocumentVersion
  ) {
    const { documentVersions } = getState();
    const documentRef = documentVersions.find(
      (documentVersionRef) => documentVersionRef.documentId === documentId
    );
    const refVersions = documentRef?.versions;
    if (documentRef && refVersions) {
      let versionToUpdateIndex = findDocumentVersionIndex(
        refVersions,
        documentVersionId,
        versionDisambiguator
      );
      if (versionToUpdateIndex === -1) {
        versionToUpdateIndex = refVersions.findIndex(
          (refVersion) => refVersion.id === documentVersionId
        );
      }
      return this.service.getDocumentVersion(documentId, documentVersionId, includeDeleted).pipe(
        tap((content) => {
          if (versionToUpdateIndex !== -1) {
            const versionCopy = { ...refVersions[versionToUpdateIndex], content };
            const refCopy = { ...documentRef, versions: [...documentRef.versions] };

            refCopy.versions.splice(versionToUpdateIndex, 1, versionCopy);
            setState(
              patch({
                documentVersions: updateItem<DocumentVersionRef>(
                  (ref) => ref?.documentId === documentId,
                  refCopy
                ),
              })
            );
          }
        })
      );
    }
    return of({});
  }

  @Action(PrintDocumentTree, { cancelUncompleted: true })
  printDocumentTree(
    { getState, setState }: StateContext<DocumentStateModel>,
    {
      menuId,
      tableOfContents,
      printLogoType,
      useLargeTenantLogo,
      includeDocumentId,
      isLandscape,
      useNewPdfService,
      filter,
      statuses,
      documentAvailability,
    }: PrintDocumentTree
  ) {
    return this.service
      .printDocumentTree(
        menuId,
        tableOfContents,
        printLogoType,
        useLargeTenantLogo,
        includeDocumentId,
        isLandscape,
        useNewPdfService,
        filter,
        statuses,
        documentAvailability
      )
      .pipe(
        tap((file: Blob) => {
          const state = getState();
          setState({
            ...state,
            documentPrintFile: file,
          });
        })
      );
  }

  @Action(PrintDocument, { cancelUncompleted: true })
  printDocument(
    { getState, setState }: StateContext<DocumentStateModel>,
    {
      menuId,
      documentId,
      tableOfContents,
      printLogoType,
      useLargeTenantLogo,
      includeDocumentId,
      includeHeaderInEveryPage,
      isLandscape,
      forcedVersionId,
      useNewPdfService,
    }: PrintDocument
  ) {
    return this.service
      .printDocument(
        menuId,
        documentId,
        tableOfContents,
        printLogoType,
        useLargeTenantLogo,
        includeDocumentId,
        includeHeaderInEveryPage,
        isLandscape,
        forcedVersionId,
        useNewPdfService
      )
      .pipe(
        tap((file: Blob) => {
          const state = getState();
          setState({
            ...state,
            documentPrintFile: file,
          });
        })
      );
  }

  @Action(PrintTextControlOnly, { cancelUncompleted: true })
  printTextControlOnly(
    { getState, setState }: StateContext<DocumentStateModel>,
    { documentId }: PrintTextControlOnly
  ) {
    return this.service.printTextControlOnly(documentId).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          textControlPrintFile: file,
        });
      })
    );
  }

  @Action(ApproveDocument, { cancelUncompleted: true })
  @OnSuccess({ message: 'documentApproved' })
  approveDocument(
    ctx: StateContext<DocumentStateModel>,
    { documentId, menuId, approvalStageId, comment, readReceipt }: ApproveDocument
  ) {
    return this.service
      .approveRejectDocument(documentId, <IDocumentApproval>{
        comment,
        approvalStageId,
        approvalStatus: ApprovalStatus.Accepted,
        readReceipt,
      })
      .pipe(
        tap(() => {
          const url = `${UserDocumentsRoutes.Overview}/${menuId}/published`;
          redirectAndBypassUnsavedDataCheck(url);
        })
      );
  }

  @Action(RejectDocument, { cancelUncompleted: true })
  @OnSuccess({ message: 'documentRejected' })
  rejectDocument(
    ctx: StateContext<DocumentStateModel>,
    { documentId, menuId, approvalStageId, comment }: RejectDocument
  ) {
    return this.service
      .approveRejectDocument(documentId, <IDocumentApproval>{
        comment,
        approvalStageId,
        approvalStatus: ApprovalStatus.Rejected,
      })
      .pipe(
        tap(() => {
          const url = `${UserDocumentsRoutes.Overview}/${menuId}/rejected`;
          redirectAndBypassUnsavedDataCheck(url);
        })
      );
  }

  @Action(EnableDocument, { cancelUncompleted: true })
  @OnSuccess({ message: 'documentEnabled' })
  enableDocument(ctx: StateContext<DocumentStateModel>, { documentId, menuId }: EnableDocument) {
    return this.service.enableDocument(documentId).pipe(
      tap(() => {
        const url = `${UserDocumentsRoutes.Overview}/${menuId}`;
        redirectAndBypassUnsavedDataCheck(url);
      })
    );
  }

  @Action(DisableDocument, { cancelUncompleted: true })
  @OnSuccess({ message: 'documentDisabled' })
  disableDocument(ctx: StateContext<DocumentStateModel>, { documentId, menuId }: DisableDocument) {
    return this.service.disableDocument(documentId).pipe(
      tap(() => {
        const url = `${UserDocumentsRoutes.Overview}/${menuId}/disabled`;
        redirectAndBypassUnsavedDataCheck(url);
      })
    );
  }

  @Action(RestoreDocument, { cancelUncompleted: true })
  @OnSuccess({ message: 'documentRestored' })
  restoreDocument(ctx: StateContext<DocumentStateModel>, { documentId, menuId }: RestoreDocument) {
    return this.service.restoreDocument(documentId).pipe(
      tap(() => {
        const url = `${UserDocumentsRoutes.Overview}/${menuId}`;
        redirectAndBypassUnsavedDataCheck(url);
      })
    );
  }

  @Action(UpdateDepartmentsReadReceiptSettings, { cancelUncompleted: true })
  @OnSuccess({ message: 'documentSettingsUpdated' })
  updateDepartmentsReadReceiptSettings(
    { getState, setState }: StateContext<DocumentStateModel>,
    { settings }: UpdateDepartmentsReadReceiptSettings
  ) {
    return this.service.updateDepartmentsReadReceiptSettings(settings).pipe(
      switchMap(() => this.service.getDepartmentsReadReceiptSettings()),
      tap((departmentReadReceiptSettings: IReadReceiptSettingsResponse[]) => {
        setState({
          ...getState(),
          departmentReadReceiptSettings,
          currentDepartmentReadReceiptSettings: this.getCurrentDepartmentReadReceiptSettings(
            departmentReadReceiptSettings
          ),
        });
      })
    );
  }

  private getCurrentDepartmentReadReceiptSettings(
    settings: IReadReceiptSettingsResponse[]
  ): IReadReceiptSettingsResponse {
    const currentDepartmentId = getDepartmentId();

    return (
      settings.find((setting) => setting.departmentId === currentDepartmentId) ?? {
        departmentId: currentDepartmentId,
        readReceiptsEnabled: false,
        changedAt: null,
        changedBy: null,
      }
    );
  }

  @Action(RequestChangeOnDocument, { cancelUncompleted: true })
  @OnSuccess({ message: 'changeRequested' })
  createChangeRequest(
    ctx: StateContext<DocumentStateModel>,
    { documentId, description, menuId }: RequestChangeOnDocument
  ) {
    return this.service.createChangeRequest(documentId, description).pipe(
      tap(() => {
        const url = `${UserDocumentsRoutes.Overview}/${menuId}`;
        redirectAndBypassUnsavedDataCheck(url);
      })
    );
  }

  @Action(AcceptDocumentChangeRequest, { cancelUncompleted: true })
  @OnSuccess({ message: 'changeRequestAccepted' })
  acceptChangeRequest(
    ctx: StateContext<DocumentStateModel>,
    { documentId, reviewComment, id, menuId }: AcceptDocumentChangeRequest
  ) {
    return this.service
      .reviewChangeRequest(documentId, reviewComment, ChangeRequestStatus.Accepted, id)
      .pipe(
        tap(() => {
          const url = `${UserDocumentsRoutes.Overview}/${menuId}`;
          redirectAndBypassUnsavedDataCheck(url);
        })
      );
  }

  @Action(RejectDocumentChangeRequest, { cancelUncompleted: true })
  @OnSuccess({ message: 'changeRequestRejected' })
  rejectChangeRequest(
    ctx: StateContext<DocumentStateModel>,
    { documentId, reviewComment, id, menuId }: RejectDocumentChangeRequest
  ) {
    return this.service
      .reviewChangeRequest(documentId, reviewComment, ChangeRequestStatus.Rejected, id)
      .pipe(
        tap(() => {
          const url = `${UserDocumentsRoutes.Overview}/${menuId}`;
          redirectAndBypassUnsavedDataCheck(url);
        })
      );
  }

  @Action(LoadDocumentCounts, { cancelUncompleted: true })
  loadDocumentCounts(
    { setState, getState }: StateContext<DocumentStateModel>,
    { menuId }: LoadDocumentCounts
  ) {
    return this.service.getDocumentCounts(menuId).pipe(
      tap((counts: IDocumentCounts) => {
        setState({
          ...getState(),
          documentCounts: counts,
        });
      })
    );
  }

  @Action(LoadDocumentNavigation, { cancelUncompleted: true })
  loadDocumentNavigation(
    { setState, getState }: StateContext<DocumentStateModel>,
    { documentId, filter }: LoadDocumentNavigation
  ) {
    return this.service.loadDocumentNavigation(documentId, filter).pipe(
      tap((navigation: IDocumentNavigationResponse) => {
        setState({
          ...getState(),
          documentNavigation: navigation,
        });
      })
    );
  }

  @Action(ClearDocumentsByMenu, { cancelUncompleted: true })
  clearDocumentsByMenu(ctx: StateContext<DocumentStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      groupedDocuments: [],
      documentsTotalPages: 0,
    });
  }

  @Action(ClearCurrentDocument, { cancelUncompleted: true })
  clearCurrentDocument(ctx: StateContext<DocumentStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      currentDocument: undefined,
      currentDocumentReadReceipts: undefined,
      documentNavigation: undefined,
    });
  }

  @Action(SetRevisionNumber, { cancelUncompleted: true })
  @OnSuccess({ message: 'revisionNumberSet' })
  setRevisionNumber(
    ctx: StateContext<DocumentStateModel>,
    { documentId, revisionNumber, comment }: SetRevisionNumber
  ) {
    return this.service.setRevisionNumber(documentId, revisionNumber, comment);
  }

  @Selector()
  static getAllDocuments(state: DocumentStateModel) {
    return state.documents;
  }

  @Selector()
  static getDocumentsByMenu(state: DocumentStateModel) {
    return state.groupedDocuments;
  }

  @Selector()
  static getDocumentsAwaitingReadReceipt(state: DocumentStateModel) {
    return state.documentsAwaitingReadReceipt;
  }

  @Selector()
  static getDocumentsTotalPages(state: DocumentStateModel) {
    return state.documentsTotalPages;
  }

  @Selector()
  static getAdminReadReceiptRequests(state: DocumentStateModel) {
    return state.adminReadReceiptRequests;
  }

  @Selector()
  static getAdminReadReceiptRequestsTotalPages(state: DocumentStateModel) {
    return state.adminReadReceiptRequestsTotalPages;
  }

  @Selector()
  static getUserDocumentReadReceipts(state: DocumentStateModel) {
    return state.userDocumentReadReceipts;
  }

  @Selector()
  static getUserDocumentReadReceiptsTotalPages(state: DocumentStateModel) {
    return state.userDocumentReadReceiptsTotalPages;
  }

  @Selector()
  static getDocumentById(state: DocumentStateModel) {
    return (id: string) => {
      return state.documents.find((document: IDocument) => document.id === id);
    };
  }

  @Selector()
  static getDocumentVersionsById(state: DocumentStateModel) {
    return (id: string) => {
      return (
        state.documentVersions.find((item: DocumentVersionRef) => item.documentId === id)
          ?.versions || []
      );
    };
  }

  @Selector()
  static getDocumentPrintFile(state: DocumentStateModel) {
    return state.documentPrintFile;
  }

  @Selector()
  static getTextControlPrintFile(state: DocumentStateModel) {
    return state.textControlPrintFile;
  }

  @Selector()
  static getCurrentDepartmentReadReceiptSettingsEnabled(state: DocumentStateModel) {
    return state.currentDepartmentReadReceiptSettings?.readReceiptsEnabled ?? false;
  }

  @Selector()
  static getCurrentDepartmentReadReceiptSettings(state: DocumentStateModel) {
    return state.currentDepartmentReadReceiptSettings;
  }

  @Selector()
  static getDepartmentReadReceiptSettings(state: DocumentStateModel) {
    return state.departmentReadReceiptSettings;
  }

  @Selector()
  static getDocumentCounts(state: DocumentStateModel) {
    return state.documentCounts;
  }

  @Selector()
  static getDocumentsForIO(state: DocumentStateModel) {
    return state.documentsForIO;
  }

  @Selector()
  static getCurrentDocument(state: DocumentStateModel) {
    return state.currentDocument;
  }

  @Selector()
  static getDocumentNavigation(state: DocumentStateModel) {
    return state.documentNavigation;
  }

  @Selector()
  static getCurrentDocumentReadReceipts(state: DocumentStateModel) {
    return state.currentDocumentReadReceipts ?? [];
  }
}
